import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { AuthService } from '../../_services/auth.service';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { CalcConfigService } from '_services/calc-config.service';
import { TrafficService } from '_services/traffic.service';
import { HelpComponent } from '../../_shared/help/help.component';
import { AsyncPipe, DatePipe, DecimalPipe } from '@angular/common';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatAnchor } from '@angular/material/button';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CURRENT_AID_YEAR,
  CURRENT_AID_YEAR_LABEL,
  formatAidYear
} from '../../_shared/aid-year/aid-year.config';
import { TrafficPeriod } from 'app/_models/traffic';
import { CohortPhraseCardComponent } from './cohort-phrase-card.component';

type ActivityMetric = 'results' | 'visitors';

interface DashboardStatus {
  hasUnpublished: boolean;
  isBehind: boolean;
  isUpToDate: boolean;
  draftUpdated: Date | null;
  publishedDate: Date | null;
  currentLabel: string;
  publishedLabel: string;
}

interface ActivitySummary {
  metric: ActivityMetric;
  monthLabel: string;
  value: number;
  unit: string;
  completionPct: number;
  vsPriorLabel: string | null;
  bars: { label: string; heightPct: number; isLatest: boolean; value: number }[];
}

interface SetupLink {
  path: string;
  title: string;
  subtitle: string;
}

interface SeasonalCard {
  icon: string;
  title: string;
  text: string;
  action: string;
  path?: string;
  href?: string;
}

const NEED_ANALYSIS_OPTIONS_URL = 'https://highered.collegeboard.org/need-analysis-options';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  imports: [
    HelpComponent,
    MatCard,
    MatCardContent,
    RouterLink,
    MatAnchor,
    CohortPhraseCardComponent,
    AsyncPipe,
    DatePipe,
    DecimalPipe
  ]
})
export class DashboardComponent implements OnInit {
  status$: Observable<DashboardStatus | null>;
  activity$: Observable<ActivitySummary | null>;
  usesInstitutionalMethod$: Observable<boolean>;
  readonly activityMetric$ = new BehaviorSubject<ActivityMetric>('results');

  readonly seasonalCards: SeasonalCard[] = [
    {
      path: '../costs',
      icon: './assets/images/icons8-money.svg',
      title: 'Costs',
      text: 'Update tuition, fees, housing, meals, books, transportation, and other cost values.',
      action: 'Update Costs'
    },
    {
      path: '../packaging',
      icon: './assets/images/icons8-open_box.svg',
      title: 'Awards / Packaging',
      text: 'Review grant, scholarship, and aid package rules.',
      action: 'Review Packaging'
    }
  ];

  readonly needAnalysisCard: SeasonalCard = {
    href: NEED_ANALYSIS_OPTIONS_URL,
    icon: './assets/images/icons8-data_sheet_filled.svg',
    title: 'Need Analysis Options',
    text:
      'Your NPC uses the IM options you’ve selected on the Need Analysis Option site to calculate contributions for your students and their families.',
    action: 'Review IM Options'
  };

  readonly relatedLinks: SetupLink[] = [
    {
      path: '../questions',
      title: 'Questions',
      subtitle: 'Student questions shown in the calculator.'
    },
    {
      path: '../populations',
      title: 'Populations',
      subtitle: 'Student groups used in rules and calculations.'
    },
    {
      path: '../tables',
      title: 'Tables',
      subtitle: 'Lookup and range values used by rules.'
    },
    {
      path: '../formulas',
      title: 'Formulas',
      subtitle: 'Custom calculation logic.'
    }
  ];

  readonly personalizationLinks: SetupLink[] = [
    {
      path: '../onboarding',
      title: 'Branding',
      subtitle: 'Logo, colors, header, and background images.'
    },
    {
      path: '../content',
      title: 'Content',
      subtitle: 'Welcome text, instructions, please-read text, and next steps.'
    }
  ];

  constructor(
    private titleService: Title,
    public auth: AuthService,
    private route: ActivatedRoute,
    public cc: CalcConfigService,
    public ts: TrafficService
  ) {
    this.titleService.setTitle('Dashboard | NPC Admin');
    this.status$ = combineLatest([this.cc.draftRuleInfo, this.cc.publishedRuleInfo]).pipe(
      map(([draft, published]) => {
        if (!draft?.aidYear || !published?.aidYear) {
          return null;
        }
        const hasUnpublished =
          draft.lastUpdated > published.publishedDate || draft.aidYear !== published.aidYear;
        const isBehind = published.aidYear < CURRENT_AID_YEAR;
        return {
          hasUnpublished,
          isBehind,
          isUpToDate: !hasUnpublished && !isBehind,
          draftUpdated: draft.lastUpdated ?? null,
          publishedDate: published.publishedDate,
          currentLabel: CURRENT_AID_YEAR_LABEL,
          publishedLabel: formatAidYear(published.aidYear)
        };
      })
    );

    this.activity$ = combineLatest([this.ts.traffic, this.activityMetric$]).pipe(
      map(([traffic, metric]) => this.buildActivity(traffic?.trafficData, metric))
    );

    this.usesInstitutionalMethod$ = this.cc.institutionalMethod$.pipe(
      map(value => !!value)
    );
  }

  ngOnInit() {
    const schoolId = this.route.snapshot.parent?.paramMap.get('schoolId') ?? '';
    this.cc.loadDraftCalcRules(schoolId);
    this.cc.loadPublishedCalcRules(schoolId);
  }

  setActivityMetric(metric: ActivityMetric) {
    this.activityMetric$.next(metric);
  }

  private periodValue(period: TrafficPeriod, metric: ActivityMetric): number {
    if (metric === 'visitors') {
      return Math.max(period.visitors ?? 0, period.starts ?? 0);
    }
    return period.results ?? 0;
  }

  private buildActivity(
    periods: TrafficPeriod[] | undefined,
    metric: ActivityMetric
  ): ActivitySummary | null {
    if (!periods?.length) {
      return null;
    }

    const latest = periods[periods.length - 1];
    const prior = periods.at(-2);
    const value = this.periodValue(latest, metric);
    const visitors = this.periodValue(latest, 'visitors');
    const spark = periods.slice(-6);
    const max = Math.max(...spark.map((p) => this.periodValue(p, metric)), 1);

    let vsPriorLabel: string | null = null;
    if (prior) {
      const delta = value - this.periodValue(prior, metric);
      const month = prior.date.toDate().toLocaleDateString('en-US', { month: 'short' });
      vsPriorLabel = delta === 0 ? `Same as ${month}` : `${delta > 0 ? '+' : ''}${delta} vs ${month}`;
    }

    return {
      metric,
      monthLabel: latest.date.toDate().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      value,
      unit: metric === 'visitors' ? 'visitors' : 'results',
      completionPct: visitors ? Math.round((latest.results / visitors) * 100) : 0,
      vsPriorLabel,
      bars: spark.map((p, i) => {
        const barValue = this.periodValue(p, metric);
        return {
          label: p.date.toDate().toLocaleDateString('en-US', { month: 'short' }),
          value: barValue,
          heightPct: Math.max(8, Math.round((barValue / max) * 100)),
          isLatest: i === spark.length - 1
        };
      })
    };
  }
}
